import { Alert, AlertDescription, AlertTitle } from '@invent/wl-ui-kit-next/ui';
import * as React from 'react';

import { ReasonDialog } from '@/design';
import {
  RECYCLE_REASON_MAX,
  runHeadline,
  runMoves,
  runRefusalWhose,
  runRefusedInDialog,
  runStoppedSentence,
  runWords,
  type IRecycleRun,
  type IRecycleTarget,
  type RecycleMove
} from '../recycle-bin';
import { HolderLink } from './recycle-press';

/**
 * Many responses moved at once (SR-470's D5 and D6) — the recycle bin list's "Restore (n)…" and
 * Lists' move of its ticked rows: one dialog and one optional reason for all of them, then
 * `runMoves`, one call per response in turn. The dialog stays open while the run is out, and
 * after it while the caller reads its list again, so the list it closes onto is current. The
 * responses are fixed as it opens: the caller's selection clears and its re-read drops the rows
 * that moved while the dialog is still up, and its title must not count them away.
 *
 * **Where an answer is said** is `runMoves`' split: a refusal every response would meet, met by
 * the first, stays in the dialog with the reason as typed; anything else — a count landed, a
 * response refused for its own state, a run stopped half-way — is the caller's to say beside its
 * list (`RecycleRunSaid`), and kept until the next press, since it names responses the re-read
 * may no longer draw.
 */
export function RecycleRunDialog({
  open,
  onOpenChange,
  move,
  targets,
  onRan
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  move: RecycleMove;
  /** The ticked rows the list still draws, with the revisions it read — taken as it opens. */
  targets: readonly IRecycleTarget[];
  /** The run's outcome, once nothing more is said in the dialog: say it, read the list again. */
  onRan: (run: IRecycleRun) => Promise<unknown>;
}) {
  const [held, setHeld] = React.useState(targets);
  const [wasOpen, setWasOpen] = React.useState(open);
  // Taken while rendering the opening, as `ReasonDialog` takes its reason, so no frame draws the
  // last opening's count.
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setHeld(targets);
  }
  const words = runWords(move, held.length);
  const confirm = async (reason: string): Promise<Nullable<string>> => {
    const run = await runMoves(move, held, reason);
    const inDialog = runRefusedInDialog(run);
    if (inDialog !== null) return inDialog;
    await onRan(run);
    return null;
  };
  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title={words.title}
      description={words.says}
      required={false}
      max={RECYCLE_REASON_MAX}
      confirmLabel={words.confirm}
      onConfirm={confirm}
    >
      <p className="text-xs text-muted-foreground">
        The reason is kept with each move in its response’s history.
      </p>
    </ReasonDialog>
  );
}

/** A run's outcome above its list: the count, then each refused response by whose it is, with
 *  "Open it" to the live response that holds a restore's slot, and where the run stopped. */
export function RecycleRunSaid({ run }: { run: IRecycleRun }) {
  const troubled = run.refused.length > 0 || run.stopped !== null;
  return (
    <Alert
      data-recycle-run
      variant={troubled ? 'destructive' : 'default'}
      role={troubled ? 'alert' : 'status'}
    >
      <AlertTitle>{runHeadline(run)}</AlertTitle>
      {troubled && (
        <AlertDescription className="grid gap-1">
          {run.refused.length > 0 && (
            <ul className="grid gap-1">
              {run.refused.map((refused, index) => (
                <li key={index} data-recycle-refused>
                  <span className="font-medium">{runRefusalWhose(refused)}</span> —{' '}
                  {refused.sentence}
                  {refused.holder && <HolderLink holder={refused.holder} />}
                </li>
              ))}
            </ul>
          )}
          {run.stopped && <p>{runStoppedSentence(run.stopped)}</p>}
        </AlertDescription>
      )}
    </Alert>
  );
}
